import { apiClient } from './apiService';
import { ConversationEntry, TranscriptEntry, SpeechRateAlert } from '../store/conversationSlice';
import { ConversationCategory, isObjectionCategory } from '../types/conversation';
import { sessionManager } from './sessionManager';

interface ConversationSessionData {
  sessionId: string;
  sessionName: string;
  startTime: Date;
  endTime: Date;
  duration: number;
  callStartTime?: Date;  // When bot actually joined the meeting
  callEndTime?: Date;    // When bot was disconnected
  callDuration?: number; // Actual call duration in seconds
  transcripts: TranscriptEntry[];
  detectedObjections: Array<{
    question: string;
    category: string;
    relevance_score: number;
    timestamp: string;
    answer?: string;
    status: string;
  }>;
  detectedFAQs: Array<{
    question: string;
    category: string;
    relevance_score: number;
    timestamp: string;
    answer?: string;
  }>;
  newInquiries: Array<{
    question: string;
    category: string;
    timestamp: string;
    answer?: string;
    relevance_score: number;
  }>;
  speechRateAnalysis: {
    alerts: SpeechRateAlert[];
  };
  landmineDetections?: any[];
  competitorDetections?: any[];
  metadata: {
    roomName?: string; // Room name from ConnectionPage
    clientId?: string;
    audioSettings?: {
      microphone?: string;
      speaker?: string;
      dualCapture: boolean;
    };
    userAgent: string;
  };
}

interface SaveSessionResponse {
  success: boolean;
  message: string;
  data?: {
    sessionId: string;
    id: string;
    statistics: any;
  };
}

interface GetSessionsResponse {
  success: boolean;
  data: ConversationSessionData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class ConversationService {
  private sessionStartTime: Date | null = null;
  private sessionId: string | null = null;
  private roomId: string | null = null;
  private clientId: string | null = null;

  /**
   * Start a new conversation session
   */
  startSession(roomId?: string, clientId?: string): void {
    // Check if session manager already has an active session
    if (sessionManager.hasActiveSession()) {
      const sessionInfo = sessionManager.getSessionInfo();
      this.sessionStartTime = sessionInfo.startTime;
      this.sessionId = sessionInfo.sessionId;
      this.roomId = sessionInfo.roomId;
      this.clientId = sessionInfo.clientId;
      
      console.log('🎬 Using existing session from SessionManager:', {
        sessionId: this.sessionId,
        startTime: this.sessionStartTime,
        roomId: this.roomId,
        clientId: this.clientId
      });
    } else {
      // Use centralized session manager for consistent IDs
      const sessionInfo = sessionManager.initializeSession();
      
      this.sessionStartTime = sessionInfo.startTime;
      this.sessionId = sessionInfo.sessionId;
      this.roomId = sessionInfo.roomId; // Same as sessionId for consistency
      this.clientId = sessionInfo.clientId;
      
      console.log('🎬 Started new conversation session (via SessionManager):', {
        sessionId: this.sessionId,
        startTime: this.sessionStartTime,
        roomId: this.roomId,
        clientId: this.clientId
      });
    }
  }

  /**
   * Get current session info
   */
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      startTime: this.sessionStartTime,
      roomId: this.roomId,
      clientId: this.clientId
    };
  }

  /**
   * Normalize timestamp to ensure consistency
   * Converts any timestamp to local timezone ISO string
   * If timestamp is invalid or missing, use session start time + index offset
   */
  private normalizeTimestamp(timestamp: string | undefined, sessionStartTime: Date, offsetMs: number = 0): string {
    let date: Date;
    
    if (timestamp) {
      try {
        // Try to parse the provided timestamp
        date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          // Invalid timestamp, use fallback
          date = new Date(sessionStartTime.getTime() + offsetMs);
        }
      } catch (e) {
        // Parse error, use fallback
        date = new Date(sessionStartTime.getTime() + offsetMs);
      }
    } else {
      // No timestamp provided, use fallback
      date = new Date(sessionStartTime.getTime() + offsetMs);
    }
    
    // Return as ISO string in local timezone
    return date.toISOString();
  }

  /**
   * Prepare conversation data for saving
   */
  prepareSessionData(
    sessionName: string,
    transcripts: TranscriptEntry[],
    entries: ConversationEntry[],
    speechRateAlerts: SpeechRateAlert[],
    landmineAlerts?: any[],
    competitorAlerts?: any[]
  ): ConversationSessionData | null {
    if (!this.sessionStartTime || !this.sessionId) {
      console.error('❌ No active session to save');
      return null;
    }

    // TypeScript assertion - we know sessionStartTime is not null after the check above
    const sessionStartTime = this.sessionStartTime!;
    const endTime = new Date();
    const duration = Math.floor((endTime.getTime() - sessionStartTime.getTime()) / 1000);

    // Get call timing from session manager
    const sessionInfo = sessionManager.getSessionInfo();
    const callStartTime = sessionInfo.callStartTime || undefined;
    const callEndTime = sessionInfo.callEndTime || undefined;
    const callDuration = sessionManager.getCallDuration() || undefined;

    // Sort entries by timestamp first to maintain chronological order
    const sortedEntries = [...entries].sort((a, b) => {
      const dateA = new Date(a.timestamp || 0).getTime();
      const dateB = new Date(b.timestamp || 0).getTime();
      return dateA - dateB;
    });

    // Separate entries by category using the enum
    // Use actual timestamps from backend (no normalization)
    const detectedObjections = sortedEntries
      .filter(e => isObjectionCategory(e.category))
      .map((e, index) => {
        // Use actual timestamp from backend, fallback to session start if missing
        const timestamp = e.timestamp ? new Date(e.timestamp).toISOString() : sessionStartTime.toISOString();
        return {
          question: e.question,
          category: e.category,
          relevance_score: e.relevance_score,
          timestamp: timestamp,
          answer: e.answer,
          status: e.status
        };
      });

    const detectedFAQs = sortedEntries
      .filter(e => e.category === ConversationCategory.FAQ)
      .map((e, index) => {
        // Use actual timestamp from backend, fallback to session start if missing
        const timestamp = e.timestamp ? new Date(e.timestamp).toISOString() : sessionStartTime.toISOString();
        return {
          question: e.question,
          category: e.category,
          relevance_score: e.relevance_score,
          timestamp: timestamp,
          answer: e.answer
        };
      });

    const newInquiries = sortedEntries
      .filter(e => e.category === ConversationCategory.NEW_INQUIRY)
      .map((e, index) => {
        // Use actual timestamp from backend, fallback to session start if missing
        const timestamp = e.timestamp ? new Date(e.timestamp).toISOString() : sessionStartTime.toISOString();
        return {
          question: e.question,
          category: e.category,
          timestamp: timestamp,
          answer: e.answer,
          relevance_score: e.relevance_score
        };
      });

    // Use actual transcript timestamps directly - no normalization
    // Sort transcripts by original timestamp first to maintain order
    const sortedTranscripts = [...transcripts].sort((a, b) => {
      const dateA = new Date(a.timestamp || 0).getTime();
      const dateB = new Date(b.timestamp || 0).getTime();
      return dateA - dateB;
    });

    const processedTranscripts = sortedTranscripts.map((transcript) => {
      // Use actual timestamp from backend
      const timestamp = transcript.timestamp ? new Date(transcript.timestamp).toISOString() : sessionStartTime.toISOString();
      return {
        ...transcript,
        timestamp: timestamp
      };
    });

    // Use actual speech rate alert timestamps directly
    const processedSpeechRateAlerts = (speechRateAlerts || []).map((alert) => {
      // Use actual timestamp from backend
      const timestamp = alert.timestamp ? new Date(alert.timestamp).toISOString() : sessionStartTime.toISOString();
      return {
        ...alert,
        timestamp: timestamp
      };
    });

    // Store landmine alerts with actual timestamps
    const processedLandmineAlerts = (landmineAlerts || []).map((alert) => {
      // Use actual timestamp from detected_at
      const alertTime = alert.detected_at ? new Date(alert.detected_at) : new Date();

      return {
        trigger: alert.trigger,
        matched_text: alert.matched_text,
        category: alert.category,
        priority: alert.priority,
        speaker: alert.speaker,
        timestamp: alertTime.toISOString(),
        detected_at: alertTime.toISOString()
      };
    });

    // Store competitor alerts with actual timestamps
    const processedCompetitorAlerts = (competitorAlerts || []).map((alert) => {
      // Use actual timestamp from detected_at
      const alertTime = alert.detected_at ? new Date(alert.detected_at) : new Date();

      return {
        competitor: alert.competitor,
        matched_text: alert.matched_text,
        confidence: alert.confidence,
        match_type: alert.match_type,
        category: alert.category,
        speaker: alert.speaker,
        timestamp: alertTime.toISOString(),
        detected_at: alertTime.toISOString()
      };
    });

    const sessionData: ConversationSessionData = {
      sessionId: this.sessionId,
      sessionName,
      startTime: sessionStartTime,
      endTime,
      duration,
      callStartTime,  // When bot actually joined the meeting
      callEndTime,    // When bot was disconnected
      callDuration,   // Actual call duration in seconds
      transcripts: processedTranscripts,
      detectedObjections,
      detectedFAQs,
      newInquiries,
      speechRateAnalysis: {
        alerts: processedSpeechRateAlerts
      },
      landmineDetections: processedLandmineAlerts,
      competitorDetections: processedCompetitorAlerts,
      metadata: {
        roomName: localStorage.getItem('salesguru_room_name') || undefined, // Get room name from localStorage
        clientId: this.clientId || undefined,
        audioSettings: {
          dualCapture: true // This can be made dynamic based on actual settings
        },
        userAgent: navigator.userAgent
      }
    };

    return sessionData;
  }

  /**
   * Save conversation session to backend
   */
  async saveSession(sessionData: ConversationSessionData): Promise<any> {
    try {
      console.log('💾 Saving conversation session...', {
        sessionId: sessionData.sessionId,
        transcriptCount: sessionData.transcripts.length,
        objectionCount: sessionData.detectedObjections.length,
        faqCount: sessionData.detectedFAQs.length,
        landmineCount: sessionData.landmineDetections?.length || 0,
        competitorCount: sessionData.competitorDetections?.length || 0,
        sessionDuration: sessionData.duration + 's (page time)',
        callDuration: sessionData.callDuration ? sessionData.callDuration + 's (actual call time)' : 'N/A',
        callStartTime: sessionData.callStartTime || 'N/A',
        callEndTime: sessionData.callEndTime || 'N/A'
      });

      // Log the actual landmine and competitor data being sent
      if (sessionData.landmineDetections && sessionData.landmineDetections.length > 0) {
        console.log('⚠️ Landmine detections being saved:', sessionData.landmineDetections);
      }
      if (sessionData.competitorDetections && sessionData.competitorDetections.length > 0) {
        console.log('🎯 Competitor detections being saved:', sessionData.competitorDetections);
      }

      const response = await apiClient.post<SaveSessionResponse>(
        '/conversations/save-session',
        sessionData
      );

      console.log('📦 Raw API response:', response);
      console.log('📦 Response data:', response.data);

      if (response?.success) {
        console.log('✅ Conversation session saved successfully:', response.data);
        // Reset session info after successful save
        this.resetSession();
      }

      return response || { success: false, message: 'No response data' };
    } catch (error: any) {
      console.error('❌ Failed to save conversation session:', error);
      throw error;
    }
  }

  /**
   * Get user's conversation sessions
   */
  async getSessions(params?: {
    page?: number;
    limit?: number;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
  }): Promise<GetSessionsResponse> {
    try {
      const response = await apiClient.get<GetSessionsResponse>('/conversations/sessions');
      return response.data || { success: false, data: [], pagination: { page: 1, limit: 10, total: 0, pages: 0 } };
    } catch (error: any) {
      console.error('❌ Failed to fetch conversation sessions:', error);
      throw error;
    }
  }

  /**
   * Get a specific conversation session
   */
  async getSession(sessionId: string): Promise<ConversationSessionData> {
    try {
      const response = await apiClient.get<{ success: boolean; data: ConversationSessionData }>(
        `/conversations/sessions/${sessionId}`
      );
      return response.data?.data || {} as ConversationSessionData;
    } catch (error: any) {
      console.error('❌ Failed to fetch conversation session:', error);
      throw error;
    }
  }

  /**
   * Update session notes
   */
  async updateNotes(sessionId: string, notes: string): Promise<void> {
    try {
      await apiClient.patch(`/conversations/sessions/${sessionId}/notes`, { notes });
      console.log('✅ Notes updated successfully');
    } catch (error: any) {
      console.error('❌ Failed to update notes:', error);
      throw error;
    }
  }

  /**
   * Add tags to session
   */
  async addTags(sessionId: string, tags: string[]): Promise<void> {
    try {
      await apiClient.patch(`/conversations/sessions/${sessionId}/tags`, { tags });
      console.log('✅ Tags added successfully');
    } catch (error: any) {
      console.error('❌ Failed to add tags:', error);
      throw error;
    }
  }

  /**
   * Delete a conversation session
   */
  async deleteSession(sessionId: string): Promise<void> {
    try {
      await apiClient.delete(`/conversations/sessions/${sessionId}`);
      console.log('✅ Session deleted successfully');
    } catch (error: any) {
      console.error('❌ Failed to delete session:', error);
      throw error;
    }
  }

  /**
   * Get session statistics
   */
  async getStatistics(startDate?: string, endDate?: string): Promise<any> {
    try {
      const response = await apiClient.get('/conversations/statistics');
      return response.data?.data || {};
    } catch (error: any) {
      console.error('❌ Failed to fetch statistics:', error);
      throw error;
    }
  }

  /**
   * Reset session info
   */
  private resetSession(): void {
    this.sessionStartTime = null;
    this.sessionId = null;
    this.roomId = null;
    this.clientId = null;
    
    // Also clear the session manager for next recording
    sessionManager.clearSession();
  }

  /**
   * End and save the current session
   */
  async endAndSaveSession(
    sessionName: string,
    transcripts: TranscriptEntry[],
    entries: ConversationEntry[],
    speechRateAlerts: SpeechRateAlert[],
    landmineAlerts?: any[],
    competitorAlerts?: any[]
  ): Promise<any> {
    const sessionData = this.prepareSessionData(
      sessionName,
      transcripts,
      entries,
      speechRateAlerts,
      landmineAlerts,
      competitorAlerts
    );

    if (!sessionData) {
      return null;
    }

    return await this.saveSession(sessionData);
  }
}

export const conversationService = new ConversationService();